import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { objectsApi } from '../api'
import { RunbookTable } from './RunbookTable'
import { FeatheredScrollArea } from '@/components/ui/feathered-scroll-area'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import type { RunbookTask } from '@/types/entities'

interface ChangeRunbookDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string
    scenario: string
    onApply: (masterRunbookId: number, customizedTasks?: RunbookTask[]) => Promise<void>
}

export function ChangeRunbookDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
    onApply,
}: ChangeRunbookDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [selectedMasterRunbook, setSelectedMasterRunbook] = useState<number | null>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [tasks, setTasks] = useState<RunbookTask[]>([])
    const [isApplying, setIsApplying] = useState(false)
    // Store the customized tasks from RunbookTable
    const customizedTasksRef = useRef<RunbookTask[]>([])

    // Fetch master runbooks
    const { data: masterRunbooksData } = useQuery({
        queryKey: ['masterRunbooks'],
        queryFn: async () => {
            const response = await objectsApi.getMasterRunbooks()
            return response.data?.data || response.data || []
        },
    })

    // Fetch master runbook info and tasks when selected
    useEffect(() => {
        if (selectedMasterRunbook) {
            objectsApi.getMasterRunbookInfo(selectedMasterRunbook)
                .then((response) => {
                    const data = response.data?.data || response.data
                    if (data.tasks && data.tasks.length > 0) {
                        const convertedTasks: RunbookTask[] = data.tasks.map((task: any) => ({
                            id: null,
                            taskNo: task.taskNo || null,
                            task: task.task || '',
                            commands: task.commands || '',
                            duration: task.duration || 0,
                            taskOffset: null, // Will be calculated
                            startTime: null, // Deprecated: not saved, calculated from taskOffset if needed for display
                            finishTime: null, // Deprecated: not saved, calculated from taskOffset if needed for display
                            dependencies: task.dependencies || (task.taskNo === 1 ? '' : '1'),
                        }))
                        
                        // Calculate TaskOffset for the tasks
                        const tasksWithOffsets = calculateRunbookTimes(convertedTasks)
                        setTasks(tasksWithOffsets)
                    } else {
                        setTasks([])
                    }
                })
                .catch((error) => {
                    console.error('Error fetching master runbook info:', error)
                    setTasks([])
                })
        } else {
            setTasks([])
        }
    }, [selectedMasterRunbook])

    const handleApply = async () => {
        if (!selectedMasterRunbook) return
        
        setShowConfirmDialog(true)
    }

    const handleConfirmApply = async () => {
        if (!selectedMasterRunbook) return
        
        setIsApplying(true)
        try {
            // Get the current tasks - use customized tasks if available, otherwise use the current tasks state
            // Always ensure tasks have calculated times before saving
            let tasksToSave: RunbookTask[] | undefined
            
            if (customizedTasksRef.current.length > 0) {
                // Use customized tasks (they should already have calculated times from RunbookTable)
                tasksToSave = customizedTasksRef.current
            } else if (tasks.length > 0) {
                // Use current tasks and ensure they have calculated times
                tasksToSave = calculateRunbookTimes(tasks)
            }
            
            await onApply(selectedMasterRunbook, tasksToSave)
            
            toast({
                title: t('common.success'),
                description: t('objects.runbookChangedSuccessfully'),
            })
            
            setShowConfirmDialog(false)
            onOpenChange(false)
            setSelectedMasterRunbook(null)
            setTasks([])
            customizedTasksRef.current = []
        } catch (error) {
            console.error('Error applying master runbook:', error)
            toast({
                title: t('common.error'),
                description: handleApiError(error),
                variant: 'destructive',
            })
        } finally {
            setIsApplying(false)
        }
    }

    const handleCancel = () => {
        setShowConfirmDialog(false)
        setSelectedMasterRunbook(null)
        setTasks([])
        customizedTasksRef.current = []
    }
    
    // Reset customized tasks when dialog closes
    useEffect(() => {
        if (!open) {
            customizedTasksRef.current = []
        }
    }, [open])

    return (
        <>
            <Dialog open={open && !showConfirmDialog} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('objects.changeRunbook')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.changeRunbookDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        <div className="space-y-2">
                            <Label>{t('objects.masterRunbook')}</Label>
                            <Select
                                value={selectedMasterRunbook?.toString() || ''}
                                onValueChange={(value) => {
                                    setSelectedMasterRunbook(value ? parseInt(value) : null)
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('objects.selectMasterRunbook')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {masterRunbooksData?.map((runbook: any) => (
                                        <SelectItem key={runbook.id} value={runbook.id.toString()}>
                                            {runbook.description}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {tasks.length > 0 && (
                            <div className="space-y-2">
                                <Label>{t('objects.runbookTasks')}</Label>
                                <div className="border rounded-md">
                                    <FeatheredScrollArea viewportClassName="max-h-[55vh]">
                                        <RunbookTable
                                            tasks={tasks}
                                            onSave={(updatedTasks) => {
                                                // Capture customized tasks when user modifies them
                                                customizedTasksRef.current = updatedTasks
                                            }}
                                            autoUpdate={true}
                                            onAutoUpdateChange={() => {}}
                                            isSaving={false}
                                            hideToolbar={true}
                                        />
                                    </FeatheredScrollArea>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end space-x-2 pt-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    onOpenChange(false)
                                    handleCancel()
                                }}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="button"
                                onClick={handleApply}
                                disabled={!selectedMasterRunbook || tasks.length === 0}
                            >
                                {t('objects.apply')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('objects.confirmChangeRunbook')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.confirmChangeRunbookDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <Alert variant="destructive" className="mt-4">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('objects.changeRunbookWarning')}
                        </AlertDescription>
                    </Alert>

                    <div className="flex justify-end space-x-2 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowConfirmDialog(false)}
                            disabled={isApplying}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleConfirmApply}
                            disabled={isApplying}
                        >
                            {isApplying ? t('objects.applying') : t('common.yes')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
